import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import useMembershipStatus from "@/hooks/useMembershipStatus";
import { faCheckCircle, faInfoCircle, faTimesCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, AlertTitle, Button, Link, Skeleton} from "@mui/material";
import { useScopedField } from "@ngt/forms-core";
import { FC, useEffect, useRef, useState } from "react";
import useIsAdminSection from "@/hooks/useIsAdminSection";
import * as Dtos from '../api/dtos';
import { faRefresh } from "@fortawesome/pro-duotone-svg-icons";
import useDebouncedValue from "@/hooks/useDebouncedValue";

interface IUseMembershipStatus {
    registrationSection?: boolean;
}

const MemberCheck: FC<IUseMembershipStatus> = ({
    registrationSection: speakerSection
}) => {
    const adminSection = useIsAdminSection();

    const {state: {value: firstName}} = useScopedField<string>("firstName", {value: true});
    const {state: {value: lastName}} = useScopedField<string>("lastName", {value: true});
    const {state: { value: id }} = useScopedField<number>("id", { value: true });
    const {state: { value: member }, context: {setValue: setMember}} = useScopedField<number>("member", { value: true });
    const {state: {value: membership}, context: {setValue: setMembership}} = useScopedField<Dtos.MembershipType>("membership", {value: true});
    const {state: {value: alreadyRegistered}, context: {setValue: setAlreadyRegistered}} = useScopedField<boolean>("alreadyRegistered", {value: true});

    const allowAutoUpdate = !adminSection || !id;

    const debouncedFirstName = useDebouncedValue(firstName);
    const debouncedLastName = useDebouncedValue(lastName);
    const { membershipType, registered, loading } = useMembershipStatus(debouncedFirstName ?? undefined, debouncedLastName ?? undefined);

    const authUser = useAuthenticatedUser<Dtos.UserDto>();

    const [deferredLoading, setDeferredLoading] = useState(false);
    const timerRef = useRef<number>();

    useEffect(() => {
        if (loading) {
            timerRef.current = window.setTimeout(() => {
                setDeferredLoading(true);
            }, 250);
        }
        else {
            if (timerRef.current) {
                window.clearTimeout(timerRef.current)
                timerRef.current = undefined;
            }

            setDeferredLoading(false);
        }
    }, [loading, setDeferredLoading, timerRef])

    useEffect(() => {
        if (!allowAutoUpdate || membershipType === member)
        {
            return
        }

        setMember(membershipType);
        
    }, [allowAutoUpdate, member, membershipType, setMembership])

    useEffect(() => {
    }, [])

    useEffect(() => {
        setMembership(member)
    }, [member, setMembership])

    useEffect(() => {
        setAlreadyRegistered(registered);

    }, [registered, setAlreadyRegistered])

    const action = allowAutoUpdate ? 
        undefined :
        (
            <Button 
                color="inherit" 
                size="small"
                sx={{
                    backgroundColor: 'unset',
                    minWidth: 'unset',
                    padding: 1
                }}
                onClick={() => {
                    setMember(membershipType)
                    setMembership(membershipType)
                }}
            >
                <FontAwesomeIcon icon={faRefresh} fixedWidth />
            </Button>
        )

    if (deferredLoading && loading) {
        return (
            <Alert
                icon={<FontAwesomeIcon icon={faInfoCircle} />}
                severity="info"
                sx={{
                    alignItems: 'center'
                }}
            >
                <AlertTitle>Membership Status &ndash; <Skeleton width={120} sx={{display: 'inline-block'}} /></AlertTitle>
                Checking membership status...
            </Alert>
        )
    }

    if (alreadyRegistered && !adminSection && !speakerSection) {
        return (
            <>
                <Alert
                    icon={<FontAwesomeIcon icon={faTimesCircle} />}
                    severity="error"
                    sx={{
                        alignItems: 'center'
                    }}
                >
                    <AlertTitle>Already Registered</AlertTitle> 
                    
                    {
                        speakerSection ?
                            <>
                                You have already registered for the ASM.
                            </>
                        :
                            <>
                                You have already registered for the ASM. If you wish to change your registration, please contact BCT on <Link href="tel:0249255255">02 4925 5255</Link>.
                            </>
                    }
                </Alert>
            </>
        )
    }

    if (membership) {
        return (
            <>
                {
                    !adminSection ?
                    <Alert
                        icon={<FontAwesomeIcon icon={faCheckCircle} />}
                        severity="success"
                        sx={{
                            alignItems: 'center'
                        }}
                    >
                        {
                            !authUser ? 
                                <>
                                    <AlertTitle>Membership Status &ndash; BCT Member</AlertTitle> 
                                    {
                                        speakerSection ?
                                            <>
                                                You are a member
                                            </>
                                        :
                                            <>
                                                Your details will be entered automatically if you login to the website to complete your registration.
                                            </>
                                    }
                                </>
                            :
                                membership == 3?
                                <>
                                    <AlertTitle>Membership Status &ndash; BCT Life Member</AlertTitle> 
                                    
                                    {
                                        speakerSection ?
                                            <>
                                                You are a member
                                            </>
                                        :
                                            <>
                                                Registration is free, you will only be charged for extras
                                            </>
                                    }
                                </>
                                :
                                <>
                                    <AlertTitle>Membership Status &ndash; BCT Member</AlertTitle> 
                                    
                                    {
                                        speakerSection ?
                                            <>
                                                You are a member
                                            </>
                                        :
                                            <>
                                                A reduced registration fee will be payable.
                                            </>
                                    }
                                </>
                        }
                    </Alert>
                    :
                    <Alert
                        icon={<FontAwesomeIcon icon={faCheckCircle} />}
                        severity="success"
                        sx={{
                            alignItems: 'center'
                        }}
                        action={action}
                    >
                        {
                            membership == 3?
                                <>
                                    <AlertTitle>Membership Status &ndash; BCT Life Member</AlertTitle> 
                                    
                                    {
                                        speakerSection ?
                                            <>
                                                You are a member
                                            </>
                                        :
                                            <>
                                                Registration is free
                                            </>
                                    }
                                </>
                                :
                                <>
                                    <AlertTitle>Membership Status &ndash; BCT Member</AlertTitle>
                                    
                                    {
                                        speakerSection ?
                                            <>
                                                You are a member
                                            </>
                                        :
                                            <>
                                                Membership pricing has been applied.
                                            </>
                                    }
                                </>
                        }
                    </Alert>
                }
            </>
        )
    }

    if (firstName && lastName) {
        return (
            <>
            {
                !adminSection ? 
                <Alert
                    icon={<FontAwesomeIcon icon={faInfoCircle} />}
                    severity="info"
                    sx={{
                        alignItems: 'center'
                    }}
                >
                    <AlertTitle>Membership Status &ndash; Non-Member</AlertTitle>
                    
                    {
                        speakerSection ?
                            <>
                                You are not a member
                            </>
                        :
                            <>
                                You are not a BCT member, If this is incorrect, please contact BCT on <Link href="tel:0249255255">02 4925 5255</Link>.
                            </>
                    }
                </Alert>
                :
                <Alert
                    icon={<FontAwesomeIcon icon={faInfoCircle} />}
                    severity="info"
                    sx={{
                        alignItems: 'center'
                    }}
                    action={action}
                >
                    <AlertTitle>Membership Status &ndash; Non-Member</AlertTitle>
                    Non-member
                </Alert>
            }
            </>
        )
    }

    return (
        <Alert
            icon={<FontAwesomeIcon icon={faInfoCircle} />}
            severity="info"
            sx={{
                alignItems: 'center'
            }}
        >
                <AlertTitle>Membership Status &ndash; Unknown</AlertTitle>
                Please enter your first and last name.
        </Alert>
    );
}

export default MemberCheck;